import type { BankedDancer } from '../api/attendance'

/**
 * <b>One panel, three readers</b> (D-757, D-764).
 *
 * A parent reads it on her dancer, the studio reads it on the dancer record,
 * and both are the same answer from the same count - `GetBankedAsync`, which
 * is the one place a bank is worked out (D-592). Its own file because the
 * third caller is where a copy would have been made, and two renderings of
 * one number drift in exactly the way that matters here: the number decides
 * whether somebody goes looking for a makeup.
 *
 * <b>Shown only when there is something</b>, because *0 banked classes* on
 * every family's screen is a line nobody reads by the third child - and a
 * credit is uncommon enough that its absence is the ordinary case.
 *
 * <b>Broken down, because a bank is not fungible</b>: a banked ballet class
 * repairs a ballet absence and nothing else (`MakeupMatch` compares the style,
 * D-401), so one total would promise a reach it has not got.
 *
 * <b>And it says there is nothing to do</b>, which is the half a number alone
 * gets wrong. A credit is spent by the studio against the first absence it
 * fits (D-592); a parent who reads *3 banked* and goes looking for a button
 * has been told half a fact.
 */
export default function BankedInHand({ banked }: { banked?: BankedDancer }) {
  /*
   * <b>Reads what it was given rather than trusting the shape.</b> An answer
   * that is not a balance - a stale payload, a route that moved, a stub in a
   * test - used to reach `kinds.map` and take the whole dancer's panel down
   * with it. A panel about a credit is never worth a white screen over her
   * attendance, so anything it cannot read is nothing to show.
   */
  if (!banked || !Array.isArray(banked.kinds)) return null
  if (!(banked.available > 0)) return null

  return (
    <div className="mb-3 flex flex-col gap-1.5 rounded-xl border border-line-2 p-3">
      <p className="text-[13px] font-semibold text-ink">
        {banked.available === 1
          ? '1 banked class in hand'
          : `${banked.available} banked classes in hand`}
      </p>
      <p className="text-[12.5px] text-ink-3">
        A class she took that she did not have to. Each one covers the first absence it fits, on
        its own &mdash; there is nothing to book.
      </p>

      <ul className="flex flex-col gap-0.5 text-[12.5px] text-ink-2">
        {banked.kinds.map((kind) => (
          <li key={kind.label} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 wrap-anywhere">{kind.label}</span>
            <span className="tabular shrink-0 text-ink-3">
              {kind.available}
              {kind.applied > 0 && <> &middot; {kind.applied} used</>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
